import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PageContainer from '../../../layouts/PageContainer';
import PageHeader from '../../../components/Nav/PageHeader';
import WifiPlanActionBar from '../../../components/Config/WifiPlanActionBar';
import { mapPlanToForm } from '../../../util/wifiPlan.mappers';
import { useConfig } from '../../../DataStore/ConfigContext';
import ActivityLoader from '../../../components/Common/ActivityLoader';

// helpers
const mapDbTypeToUi = (plan_type) => {
  if (!plan_type) return 'time';
  const k = String(plan_type).toLowerCase();
  if (k.startsWith('data')) return 'data';
  if (k.startsWith('hybrid')) return 'hybrid';
  return 'time';
};
const toNum = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

const toInt = (v, d = null) => {
  if (v === '' || v === null || typeof v === 'undefined') return d;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? d : n;
};

const toKbpsFromMbps = (mbps) => {
  const n = toInt(mbps, 0);
  if (n <= 0) return null;
  return n * 1000;
};

// map API -> table row
const mapApiPlanToRow = (p, idx = 0) => ({
  id: p.id,
  name: p.name || '',
  type: mapDbTypeToUi(p.plan_type),
  description: p.description || null,
  maxDevices: toNum(p.max_devices),

  // limits
  durationMinutes: toNum(p.duration_minutes),
  quotaMB: toNum(p.data_limit_mb),
  isUnlimited: String(p.is_unlimited ?? '0') === '1',

  // speed
  downloadMbps: toNum(p.download_mbps),
  uploadMbps: toNum(p.upload_mbps),
  rateLimit: p.mikrotik_rate_limit || null,
  speedKbps: toKbpsFromMbps(p.download_mbps),

  // misc
  price: toNum(p.price) ?? 0,
  currency: p.currency || 'UGX',
  portalVisible: String(p.is_visible ?? '1') === '1',
  active: String(p.status || '').toLowerCase() === 'active',
  createdAt: p.created_at,
  updatedAt: p.updated_at,

  sort: idx + 1, // fallback if you don’t have sort_order
});

const fmtGB = (mb) => {
  if (mb == null) return null;
  const gb = mb / 1024;
  return `${gb >= 10 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
};

const fmtSpeed = (down, up, rateLimit) => {
  if (down != null || up != null) {
    return `↓${down ?? 0} / ↑${up ?? 0} Mbps`;
  }
  return rateLimit || '—';
};

const fmtLimits = (isUnlimited, minutes, mb) => {
  if (isUnlimited) return 'Unlimited';
  const parts = [];
  if (minutes) parts.push(`${minutes} min`);
  if (mb) parts.push(fmtGB(mb));
  return parts.length ? parts.join(' • ') : '—';
};


export default function WifiPlanPage() {
  const { wifiPlans, getWifiPlans, isLoading: configLoading, selectWifiPlan, selectedWifiPlan } = useConfig(); // if you expose isLoading
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [filter, setFilter] = useState({ type: 'all', status: 'all' });
  const navigate = useNavigate();
  const { state } = useLocation();

  // edit
  useEffect(()=> { if(selectedWifiPlan && showEdit) navigate(`/settings/plans-edit`);}, [selectedWifiPlan, navigate, showEdit])

  // fetch
  useEffect(() => { getWifiPlans(); }, [getWifiPlans]);

  // flash
  useEffect(() => { if (state?.flash) setStatus(state.flash); }, [state]);

  // adapt wifiPlans -> table rows
  useEffect(() => {
    if (Array.isArray(wifiPlans)) {
      const rows = wifiPlans.map((p, i) => mapApiPlanToRow(p, i));
      setPlans(rows);
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [wifiPlans]);

  const filtered = useMemo(() => {
    let list = [...plans];
    if (filter.type !== 'all') list = list.filter((p) => p.type === filter.type);
    if (filter.status !== 'all') list = list.filter((p) => (filter.status === 'active' ? p.active : !p.active));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) => [p.name, p.description].some((x) => (x || '').toLowerCase().includes(q)));
    }
    return list.sort((a, b) => (a.sort || 0) - (b.sort || 0) || a.name.localeCompare(b.name));
  }, [plans, filter, query]);

  const handleEdit = (plan) => {
    selectWifiPlan(plan);
    setShowEdit(true);
  }
  const handleDuplicate = (plan) =>
    navigate(`/settings/plans-new`, {
      state: {
        initial: mapPlanToForm({
          ...plan,
          id: undefined,
          name: `${plan.name} (copy)`,
          status: 'inactive',
          sort: (plan.sort || 0) + 1,
        }),
      },
    });
  const handleToggleActive = (id) =>
    setPlans((ls) => ls.map((p) => (p.id === id ? { ...p, active: !p.active } : p)));
  const handleArchive = (id) => {
    if (!window.confirm('Archive this plan?')) return;
    setPlans((ls) => ls.filter((p) => p.id !== id));
  };


  return (
    <PageContainer>
      <PageHeader
        title="WiFi Plans"
        breadcrumb={
          <>
            <li className="breadcrumb-item"><Link to="/settings">Settings</Link></li>
            <li className="breadcrumb-item active">WiFi Plans</li>
          </>
        }
      />

      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-info mb-0">
              Manage existing plans; use “New Plan” to create.
            </div>
          </div>
        </div>

        <WifiPlanActionBar
          showForm={false}
          onNew={() => navigate('/settings/plans-new')}
          onToggleForm={() => {}}
          query={query}
          onQueryChange={setQuery}
          filterType={filter.type}
          onFilterTypeChange={(v) => setFilter((f) => ({ ...f, type: v }))}
          filterStatus={filter.status}
          onFilterStatusChange={(v) => setFilter((f) => ({ ...f, status: v }))}
        />

        {status?.msg && (
          <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            {status.msg}
          </div>
        )}

        <div className="card mt-2">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Existing Plans</h6>
              {(loading || configLoading) && <span className="spinner-border spinner-border-sm" />}
            </div>

            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0">
               <thead className="thead-light">
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th className="text-right">Limits</th>
                  <th className="text-right">Speed</th>
                  <th className="text-right">Price</th>
                  <th>Portal</th>
                  <th>Status</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, idx) => (
                  <tr key={p.id}>
                    <td>{p.sort ?? idx + 1}</td>
                    <td>
                      <div className="font-weight-600">{p.name}</div>
                      {p.description ? <div className="small text-muted">{p.description}</div> : null}
                    </td>
                    <td className="text-capitalize">{p.type}</td>

                    {/* Limits: Unlimited or duration/data */}
                    <td className="text-right">
                      {p.isUnlimited ? (
                        <span className="badge badge-info">Unlimited</span>
                      ) : (
                        <span>{fmtLimits(p.isUnlimited, p.durationMinutes, p.quotaMB)}</span>
                      )}
                      <br/>Max Devices: {p.maxDevices || '—'}
                    </td>

                    {/* Speed: ↓/↑ Mbps or mikrotik_rate_limit */}
                    <td className="text-right">
                      {fmtSpeed(p.downloadMbps, p.uploadMbps, p.rateLimit)}
                    </td>

                    <td className="text-right">
                      {(p.price || 0).toLocaleString()} {p.currency || 'UGX'}
                    </td>
                    <td>
                      {p.portalVisible ? (
                        <span className="badge badge-success">Shown</span>
                      ) : (
                        <span className="badge badge-secondary">Hidden</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`btn btn-xs ${p.active ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => handleToggleActive(p.id)}
                      >
                        {p.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-primary" onClick={() => handleEdit(p)}>
                          <i className="fa fa-edit mr-1" /> Edit
                        </button>
                        <button className="btn btn-outline-secondary" onClick={() => handleDuplicate(p)}>
                          <i className="fa fa-copy mr-1" /> Duplicate
                        </button>
                        <button className="btn btn-outline-danger" onClick={() => handleArchive(p.id)}>
                          <i className="fa fa-archive mr-1" /> Archive
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>

              {!Array.isArray(wifiPlans) && <ActivityLoader />}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
